import { initializeSignalRNotifications } from '../../apiCalls'
import * as SignalR from '@microsoft/signalr'
import { NegotiateApi, SignalREvents } from '../../model/signalRNotifications'

interface Handler {
	methodName: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	methodHandler: (...args: any[]) => void
}

/* eslint-disable no-console */
export class SignalRHub {
	private static connection: SignalR.HubConnection

	private static renewConnectionTimeoutId?: ReturnType<typeof setTimeout>

	private static handlers: Handler[] = []

	private static retrying = false

	static getConnectionId(): string | null {
		return this.connection?.connectionId ?? null
	}

	/**
	 * Negotiate and establish a connection with signalR.
	 * In this implementation we use a single hub for all signalR communication.
	 **/
	static async signalRConnectAsync() {
		// Clean up in case this is a retry/reconnect
		if (this.renewConnectionTimeoutId) {
			clearTimeout(this.renewConnectionTimeoutId)
		}

		// If there is an active connection disconnect it before trying to negotiate a new one
		if (SignalRHub.connection) {
			await SignalRHub.signalRDisconnectAsync()
		}

		SignalRHub.retrying = false

		let data: NegotiateApi | null = null
		try {
			const response = await initializeSignalRNotifications()
			data = response as NegotiateApi
		} catch (error: unknown) {
			console.warn('[SignalRHub] Negotiate failed.', error)
		}

		if (!data?.accessToken) {
			console.warn(
				'[SignalRHub] Could not negotiate SignalR connection. Will retry in 60 seconds.',
			)
			this.renewConnectionTimeoutId = setTimeout(
				async () => SignalRHub.signalRConnectAsync(),
				60000,
			)
			return
		}

		// Build the connection to the SignalR hub
		// The default auto reconnect is [0, 2000, 10000, 30000]
		SignalRHub.connection = new SignalR.HubConnectionBuilder()
			.withUrl(data!.url, {
				accessTokenFactory: () => data!.accessToken,
				transport: SignalR.HttpTransportType.WebSockets,
			})
			.configureLogging(SignalR.LogLevel.Warning)
			.withAutomaticReconnect([0, 3000, 5000])
			.build()

		// signalR Connection onclose.
		SignalRHub.connection.onclose((error?: Error) => {
			console.warn(
				'[SignalRHub] Connection onclose.',
				SignalRHub.connection.connectionId,
				SignalRHub.connection.state,
				error,
			)

			// Keep trying to negotiate a new signalr service if the connection was closed after reconnect was tried. Make sure to keep the user in the same groups.
			if (SignalRHub.retrying) {
				SignalRHub.signalRConnectAsync()
			} else {
				// Notify connection disconnected
				SignalRHub.handlers
					.find(h => h.methodName === SignalREvents.SignalRDisconnected)
					?.methodHandler()
			}
		})

		// SignalR Connection onreconnecting.
		// More info: https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-6.0&tabs=visual-studio#automatically-reconnect
		SignalRHub.connection.onreconnecting((error?: Error) => {
			SignalRHub.retrying = true
			console.warn(
				'[SignalRHub] Connection onreconnecting.',
				SignalRHub.connection.connectionId,
				SignalRHub.connection.state,
				error,
			)
			// Notify connection disconnected
			SignalRHub.handlers
				.find(h => h.methodName === SignalREvents.SignalRDisconnected)
				?.methodHandler()
		})

		// signalR Connection onreconnected.
		SignalRHub.connection.onreconnected((connectionId?: string) => {
			SignalRHub.retrying = false
			// Notify connection established
			SignalRHub.handlers
				.find(h => h.methodName === SignalREvents.SignalRConnected)
				?.methodHandler(connectionId)
			console.warn(
				'[SignalRHub] Connection onreconnected.',
				SignalRHub.connection.connectionId,
				SignalRHub.connection.state,
				connectionId,
			)
		})

		try {
			await SignalRHub.connection.start()
		} catch (error: unknown) {
			console.warn(
				'[SignalRHub] Connection start failed. Will retry in 30 seconds.',
				error,
			)
			if (this.renewConnectionTimeoutId) {
				clearTimeout(this.renewConnectionTimeoutId)
			}
			this.renewConnectionTimeoutId = setTimeout(
				async () => SignalRHub.signalRConnectAsync(),
				30000,
			)
			return
		}

		console.info(
			'[SignalRHub] Connection started.',
			SignalRHub.connection.connectionId,
			SignalRHub.connection.state,
		)

		// Notify connection established
		SignalRHub.handlers
			.find(h => h.methodName === SignalREvents.SignalRConnected)
			?.methodHandler(SignalRHub.connection.connectionId)

		// Wire/Rewire existing message handlers
		if (SignalRHub.handlers.length > 0) {
			console.info(
				`[SignalRHub] Rewiring ${SignalRHub.handlers.length} client methods.`,
			)

			SignalRHub.handlers
				.filter(h => !h.methodName.startsWith('SignalR'))
				.forEach(handler =>
					SignalRHub.connection.on(handler.methodName, handler.methodHandler),
				)
		}
	}

	/**
	 * Disconnect.
	 */
	static async signalRDisconnectAsync() {
		SignalRHub.retrying = false
		if (SignalRHub.connection) {
			await SignalRHub.connection.stop()
			if (this.renewConnectionTimeoutId) {
				clearTimeout(this.renewConnectionTimeoutId)
			}

			console.info('[SignalRHub] Connection stopped.')
		}
	}

	/** Registers a handler that will be invoked when the hub method with the specified method name is invoked.
	 *  It's a wrapper around connection.on that keeps the handlers when connection changes.
	 *
	 * @param {string} methodName The name of the hub method to define.
	 * @param {Function} newMethod The handler that will be raised when the hub method is invoked.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static on(methodName: string, newMethod: (...args: any[]) => any): void {
		if (SignalRHub.connection) {
			SignalRHub.connection.on(methodName, newMethod)
		}
		SignalRHub.handlers.push({
			methodName: methodName,
			methodHandler: newMethod,
		})
	}

	/** Removes all handlers for the specified hub method.
	 *  It's a wrapper around connection.off and doesn't wire the handler after a new negotiate.
	 *
	 * @param {string} methodName The name of the method to remove handlers for.
	 */
	static off(methodName: string): void {
		if (SignalRHub.connection) {
			SignalRHub.connection.off(methodName)
		}
		SignalRHub.handlers = SignalRHub.handlers.filter(item => {
			return item.methodName !== methodName
		})
	}
}
/* eslint-enable no-console */
